/**
 * The small status/priority/type labels used across the list and the detail.
 *
 * Every one of them translates a machine code from the API — the server's own
 * `LocalizedString.text` is an English reference rendering and is deliberately
 * never displayed. The single exception is `UNKNOWN`: an Ivanti administrator
 * can add a status the portal has no code for, and the contract's answer is to
 * show the verbatim `provider_status` rather than hide the ticket behind a
 * meaningless "Unknown".
 *
 * Status is the loudest thing on a ticket row, so it is the one badge whose
 * wording and colour both depend on the ticket's `kind`: an incident is
 * "Logged → In progress → Resolved", a service request is "Submitted → In
 * procurement → Delivered". Same enum on the wire, two vocabularies on screen.
 */

import { Icon, Package, TriangleAlert } from "@norican/icons";
import { Badge, cn } from "@norican/react";

import { useT } from "@/i18n";

import type {
  TicketApproval,
  TicketKind,
  TicketPriorityCode,
  TicketStatus,
  TicketStatusCode,
} from "../api/contract";
import {
  approvalTone,
  kindIconClass,
  priorityTone,
  priorityVariant,
  statusTone,
  statusVariant,
} from "../lib/ticket-display";

/** The icon that stands for each kind of ticket. */
const KIND_ICON = {
  INCIDENT: TriangleAlert,
  SERVICE_REQUEST: Package,
} as const;

/**
 * The status label, in the vocabulary of the ticket's kind.
 *
 * Two complete catalogues rather than a shared one with holes: `status.*` reads
 * the incident lifecycle, `requestStatus.*` the service-request one. Both cover
 * every `TicketStatusCode`, so neither can render blank.
 */
export function useStatusLabel() {
  const { t } = useT("tickets");

  return (code: TicketStatusCode, kind: TicketKind): string =>
    kind === "SERVICE_REQUEST" ? t(`requestStatus.${code}`) : t(`status.${code}`);
}

export interface TicketStatusBadgeProps {
  status: TicketStatus;
  /** The ticket's kind — it decides both the wording and the colour. */
  kind: TicketKind;
  size?: "xs" | "sm" | "md";
  className?: string;
}

export function TicketStatusBadge({
  status,
  kind,
  size = "sm",
  className,
}: TicketStatusBadgeProps) {
  const statusLabel = useStatusLabel();
  const label =
    status.code === "UNKNOWN" && status.provider_status.length > 0
      ? status.provider_status
      : statusLabel(status.code, kind);

  return (
    <Badge
      tone={statusTone(status.code, kind)}
      variant={statusVariant(status.code, status.is_final)}
      size={size}
      className={cn("whitespace-nowrap", className)}
    >
      {label}
    </Badge>
  );
}

export interface TicketPriorityBadgeProps {
  code: TicketPriorityCode;
  size?: "xs" | "sm" | "md";
}

export function TicketPriorityBadge({ code, size = "sm" }: TicketPriorityBadgeProps) {
  const { t } = useT("tickets");
  return (
    <Badge
      tone={priorityTone(code)}
      variant={priorityVariant(code)}
      size={size}
      className="inline-flex items-center gap-1 whitespace-nowrap"
    >
      {code === "P1" ? <Icon icon={TriangleAlert} size="xs" /> : null}
      {t(`priority.${code}`)}
    </Badge>
  );
}

export interface TicketKindBadgeProps {
  kind: TicketKind;
  size?: "xs" | "sm" | "md";
}

export function TicketKindBadge({ kind, size = "sm" }: TicketKindBadgeProps) {
  const { t } = useT("tickets");
  const icon = KIND_ICON[kind];

  return (
    <Badge
      tone="neutral"
      variant="outline"
      size={size}
      shape="rounded"
      className="inline-flex items-center gap-1.5"
    >
      <Icon icon={icon} size="xs" className={kindIconClass(kind)} />
      {t(`kind.${kind}`)}
    </Badge>
  );
}

export interface ApprovalStatusBadgeProps {
  approval: TicketApproval;
}

export function ApprovalStatusBadge({ approval }: ApprovalStatusBadgeProps) {
  const { t } = useT("tickets");
  return (
    <Badge tone={approvalTone(approval.status_code)} variant="subtle" size="sm">
      {t(`approval.${approval.status_code}`)}
    </Badge>
  );
}
